import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { bulkPercent } from "@/lib/discounts";
import { trackPixelEvent } from "@/components/MetaPixel";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const load = () => {
  try {
    return JSON.parse(localStorage.getItem("tlf-cart") || "[]");
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("tlf-cart", JSON.stringify(items));
  }, [items]);

  const addItem = (item) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.key === item.key);
      if (existing) {
        return prev.map((i) =>
          i.key === item.key ? { ...i, qty: i.qty + item.qty } : i
        );
      }
      return [...prev, item];
    });
    trackPixelEvent("AddToCart", {
      content_name: item.name,
      content_ids: [item.slug],
      value: Number(((item.lineTotal != null ? item.lineTotal : item.unitPrice * item.qty) + (item.rushFee || 0)).toFixed(2)),
      currency: "JMD",
    });
    toast.success("Added to cart", { description: item.name });
  };

  const updateQty = (key, delta) =>
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: i.qty + delta } : i))
        .filter((i) => i.qty > 0)
    );

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const lineDiscount = (item) => bulkPercent(item.categorySlug, item.qty);

  const { subtotal, count, discount, net, rushTotal } = useMemo(() => {
    let sub = 0;
    let disc = 0;
    let n = 0;
    let rushT = 0;
    for (const i of items) {
      const gross = i.lineTotal != null ? i.lineTotal : i.unitPrice * i.qty;
      const rush = i.rushFee || 0;
      // Bulk discount applies to the printing/customization portion only — never garment or size surcharges.
      const discountBase =
        i.discountBase != null ? i.discountBase : i.discountUnit != null ? i.discountUnit * i.qty : gross;
      sub += gross + rush;
      rushT += rush;
      disc += (discountBase * bulkPercent(i.categorySlug, i.qty)) / 100;
      n += i.qty;
    }
    return { subtotal: sub, count: n, discount: disc, net: sub - disc, rushTotal: rushT };
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, subtotal, count, discount, net, rushTotal, lineDiscount, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};
